import React, { useEffect, useRef, useState } from "react";
import {
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import { makeStyles } from "tss-react/mui";
import CurveButton from "components/Elements/CurveButton";
import {
	generateReportNow,
	getReportJobStatus,
	JobState,
} from "services/reportSubscriptions";
import { consumeJobResult } from "helpers/reportSubscriptions";

// Many reports complete in under 5 seconds, so poll fast at first then back
// off so we don't hammer the API for slower jobs.
const FAST_POLL_MS = 1000;
const FAST_POLL_DURATION_MS = 5000;
const SLOW_POLL_MS = 3000;
const TERMINAL_STATES = new Set([JobState.Succeeded, JobState.Failed, JobState.Unknown]);

const COLOUR_PROGRESS = "#307ad6";
const COLOUR_SUCCESS = "#23bb79";
const COLOUR_ERROR = "#E31212";
const ICON_SIZE = "9rem";

const nextPollDelay = (startedAt) =>
	Date.now() - startedAt < FAST_POLL_DURATION_MS ? FAST_POLL_MS : SLOW_POLL_MS;

// Title-bar / button colour comes from the current job state. Mirrors the
// SuccessDialog (green) / ErrorDialog (red) pattern used elsewhere in the app
// so the affordance is recognisable.
const colourForState = (state) => {
	if (state === JobState.Succeeded) return COLOUR_SUCCESS;
	if (state === JobState.Failed || state === JobState.Unknown) return COLOUR_ERROR;
	return COLOUR_PROGRESS;
};

const useStyles = makeStyles()(() => ({
	dialogTitle: {
		color: "#FFFFFF",
		display: "flex",
		justifyContent: "center",
		padding: "24px 24px 16px",
	},
	subtitle: {
		textAlign: "center",
		fontFamily: "Roboto Condensed",
		fontWeight: 600,
		fontSize: "15px",
		color: "#4b5563",
		padding: "12px 24px 0",
	},
	body: {
		textAlign: "center",
		padding: "16px 24px 24px",
	},
	bodyHeading: {
		fontFamily: "Roboto Condensed",
		fontWeight: 700,
		fontSize: "20px",
		margin: 0,
	},
	bodyError: {
		marginTop: "12px",
		fontSize: "13px",
		color: "#b91c1c",
	},
	rejectionDetail: {
		marginTop: "12px",
		fontSize: "13px",
		color: "#4b5563",
		lineHeight: 1.5,
	},
	rejectionCounts: {
		marginTop: "8px",
		fontSize: "13px",
		color: "#4b5563",
	},
	dialogAction: {
		display: "flex",
		justifyContent: "center",
		paddingBottom: "20px",
	},
}));

const STATE_MESSAGES = {
	[JobState.Pending]: "Queued — generating now…",
	[JobState.Processing]: "Generating your report…",
	[JobState.Succeeded]: "Report ready.",
	[JobState.Failed]: "We couldn't generate the report.",
	[JobState.Unknown]: "Status unavailable.",
};

const successMessage = ({ downloaded, emailed }) => {
	if (downloaded && emailed)
		return "Success — your report has been downloaded. We've also emailed you a copy.";
	if (downloaded) return "Success — your report has been downloaded.";
	if (emailed) return "Success — we've emailed you a download link.";
	return "Success — your report is ready.";
};

// A pre-flight size-guard rejection (HTTP 400 from /api/reports/generate)
// always includes both `limit` and `estimatedRowCount`. Generic 400s won't have
// those fields, so we can branch on the shape alone without coupling to status.
const isSizeRejection = (data) =>
	data &&
	typeof data.limit === "number" &&
	typeof data.estimatedRowCount === "number";

// Self-contained dialog for the on-demand "generate report" flow. The caller
// supplies the request body and a friendly report name; the dialog POSTs
// /api/reports/generate, polls /api/Jobs/{jobId}/status until terminal, and
// shows spinner / check / error states inline. Closing the dialog stops the
// poll — the backend job still runs to completion regardless.
const ReportGenerationDialog = ({ open, onClose, reportName, body }) => {
	const { classes } = useStyles();
	const [state, setState] = useState(JobState.Pending);
	const [error, setError] = useState(null);
	const [rejection, setRejection] = useState(null);
	const [downloaded, setDownloaded] = useState(false);
	// Set when the API forces a large report onto email delivery instead of the
	// interactive download; carries the server's "we'll email it" message and
	// suppresses polling (EMI3-1405).
	const [emailNotice, setEmailNotice] = useState(null);
	const pollRef = useRef(null);
	const startedRef = useRef(false);
	const consumedRef = useRef(false);

	useEffect(() => {
		if (!open) return undefined;
		if (startedRef.current) return undefined;
		startedRef.current = true;
		consumedRef.current = false;
		setState(JobState.Pending);
		setError(null);
		setRejection(null);
		setDownloaded(false);
		setEmailNotice(null);

		let cancelled = false;

		const startPolling = (jobId) => {
			const startedAt = Date.now();
			const schedule = (delay) => {
				pollRef.current = setTimeout(async () => {
					if (cancelled) return;
					const res = await getReportJobStatus(jobId);
					if (cancelled) return;
					if (res?.status && res.data) {
						const next = res.data.state;
						setState(next);
						if (next === JobState.Failed) {
							setError(res.data.exceptionMessage || res.data.message || null);
						}
						if (next === JobState.Succeeded && !consumedRef.current) {
							consumedRef.current = true;
							const consumed = consumeJobResult(res.data.result);
							if (consumed) setDownloaded(true);
						}
						if (TERMINAL_STATES.has(next)) return;
					}
					schedule(nextPollDelay(startedAt));
				}, delay);
			};
			schedule(FAST_POLL_MS);
		};

		(async () => {
			const res = await generateReportNow(body);
			if (cancelled) return;
			if (!res?.status || !res.data?.jobId) {
				setState(JobState.Failed);
				if (isSizeRejection(res?.data)) {
					setRejection({
						message: res.data.error,
						reportLabel: res.data.reportLabel,
						estimatedRowCount: res.data.estimatedRowCount,
						limit: res.data.limit,
					});
				} else {
					setError(res?.data?.error || "Could not start the report.");
				}
				return;
			}

			// The API forces a large-but-allowed report onto email delivery even
			// when we asked for a download (body.sendEmail === false). Don't make
			// the user wait on a poll for a render that could take minutes -
			// acknowledge that it's coming by email and stop here (EMI3-1405).
			const forcedEmail =
				res.data.emailed === true && body?.sendEmail !== true;
			if (forcedEmail) {
				setEmailNotice(
					res.data.message ||
						"This report is large, so we'll email it to you when it's ready.",
				);
				setState(JobState.Succeeded);
				return;
			}

			startPolling(res.data.jobId);
		})();

		return () => {
			cancelled = true;
			if (pollRef.current) {
				clearTimeout(pollRef.current);
				pollRef.current = null;
			}
			startedRef.current = false;
		};
	}, [open, body]);

	const isInFlight = state === JobState.Pending || state === JobState.Processing;
	const isSuccess = state === JobState.Succeeded;
	// Default to "not emailed" when sendEmail isn't explicitly true so a missing
	// body or unset flag never produces the misleading "we've emailed you" copy.
	const emailed = body?.sendEmail === true;
	const message = emailNotice
		? emailNotice
		: isSuccess
			? successMessage({ downloaded, emailed })
			: rejection
				? "Filter too broad"
				: STATE_MESSAGES[state] || "";
	const colour = colourForState(state);
	const subtitle = reportName;
	const formattedEstimate = rejection
		? rejection.estimatedRowCount.toLocaleString()
		: null;
	const formattedLimit = rejection
		? rejection.limit.toLocaleString()
		: null;

	const titleIcon = isInFlight ? (
		<CircularProgress
			size={ICON_SIZE}
			thickness={4}
			sx={{ color: "#FFFFFF" }}
		/>
	) : isSuccess ? (
		<CheckCircleOutline style={{ fontSize: ICON_SIZE }} />
	) : (
		<CancelIcon style={{ fontSize: ICON_SIZE }} />
	);

	return (
		<Dialog
			open={open}
			onClose={isInFlight ? undefined : onClose}
			fullWidth
			style={{ position: "absolute" }}
		>
			<DialogTitle
				className={classes.dialogTitle}
				sx={{ backgroundColor: colour }}
			>
				{titleIcon}
			</DialogTitle>
			<div className={classes.subtitle}>{subtitle}</div>
			<DialogContent className={classes.body}>
				<h2 className={classes.bodyHeading}>{message}</h2>
				{rejection ? (
					<>
						<div className={classes.rejectionDetail}>
							{rejection.message ||
								"Your filter would include more records than we can render. Please narrow it (e.g. date range, site, department, status) and try again."}
						</div>
						<div className={classes.rejectionCounts}>
							Estimated <strong>{formattedEstimate}</strong> records —
							limit is <strong>{formattedLimit}</strong>.
						</div>
					</>
				) : error ? (
					<div className={classes.bodyError}>{error}</div>
				) : null}
			</DialogContent>
			<DialogActions className={classes.dialogAction}>
				<CurveButton
					style={{ backgroundColor: colour }}
					onClick={onClose}
					disabled={isInFlight}
				>
					{isInFlight ? "Working…" : "Dismiss"}
				</CurveButton>
			</DialogActions>
		</Dialog>
	);
};

export default ReportGenerationDialog;
